/**
 * Layout de páginas autenticadas: sidebar de navegación + header + contenido.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { Outlet } from "react-router-dom";
import { AppHeader } from "../components/AppHeader";
import { AppSidebar } from "../components/AppSidebar";
import { useBase } from "../auth/TenantBrandingProvider";

const COLLAPSE_KEY = "nav:collapsed";

export function AppLayout() {
  // El prefijo de los enlaces: "/mi-salud" en el dominio de la plataforma y ""
  // cuando el cliente entra por el suyo. Sale de la URL, que es la única
  // fuente que no puede equivocarse: si no hay slug en la ruta, es que no va.
  //
  // Antes se caía al branding o al usuario cuando faltaba el slug, y eso con
  // dominio propio metía "/mi-salud" delante de cada enlace justo donde no
  // tiene que salir.
  const base = useBase();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === "1",
  );

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // Con el drawer abierto el fondo no debe scrollear.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Al cerrar el drawer el foco vuelve al botón que lo abrió, si no el teclado
  // queda huérfano al principio del documento.
  const mobileNavButtonRef = useRef<HTMLButtonElement>(null);
  const closeMobile = useCallback(() => {
    setMobileOpen((wasOpen) => {
      if (wasOpen) mobileNavButtonRef.current?.focus();
      return false;
    });
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:shadow-lg focus:ring-2 focus:ring-slate-400"
      >
        Saltar al contenido
      </a>

      <div className="flex">
        <AppSidebar
          base={base}
          mobileOpen={mobileOpen}
          onMobileClose={closeMobile}
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed((c) => !c)}
        />

        {/* min-w-0 para que las tablas anchas scrolleen dentro del main
            en vez de empujar el layout entero. */}
        <div className="flex min-w-0 flex-1 flex-col">
          <AppHeader
              base={base}
            onOpenMobileNav={() => setMobileOpen(true)}
            mobileNavButtonRef={mobileNavButtonRef}
          />
          <main id="contenido" className="mx-auto w-full max-w-screen-2xl px-4 py-8 sm:px-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
